/**
 * OAuth2 proxy auth client.
 *
 * Login flow:
 *   1. browser → GET /oauth2/authorize (iam-gateway)
 *   2. iam-gateway → GET /oauth2/authorize (Keycloak)
 *   3. user authenticates on Keycloak
 *   4. Keycloak → GET /oauth2/callback (iam-gateway)
 *   5. iam-gateway → GET {tenantOrigin}/callback#access_token=...
 *   6. CallbackPage stores tokens and redirects to the app
 */

const IAM_URL = import.meta.env.VITE_IAM_API_URL || 'https://iam.sudosys.org';
const KEYCLOAK_URL =
  import.meta.env.VITE_KEYCLOAK_URL || 'https://sso.sudosys.org';
const REALM = import.meta.env.VITE_KEYCLOAK_REALM || 'sudosys';
const CLIENT_ID = import.meta.env.VITE_KEYCLOAK_CLIENT_ID || 'ui';

const TOKEN_KEY = 'auth_access_token';
const REFRESH_TOKEN_KEY = 'auth_refresh_token';
const ID_TOKEN_KEY = 'auth_id_token';

// ---- Token storage ----

export const storeTokens = (
  accessToken: string,
  refreshToken: string,
  idToken: string
) => {
  sessionStorage.setItem(TOKEN_KEY, accessToken);
  sessionStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
  sessionStorage.setItem(ID_TOKEN_KEY, idToken);
};

export const clearTokens = () => {
  sessionStorage.removeItem(TOKEN_KEY);
  sessionStorage.removeItem(REFRESH_TOKEN_KEY);
  sessionStorage.removeItem(ID_TOKEN_KEY);
};

export const getToken = (): string | undefined =>
  sessionStorage.getItem(TOKEN_KEY) ?? undefined;
export const getRefreshToken = (): string | undefined =>
  sessionStorage.getItem(REFRESH_TOKEN_KEY) ?? undefined;

// ---- JWT helpers ----

function parseJwtPayload(token: string): Record<string, unknown> | null {
  try {
    const payload = token.split('.')[1];
    return JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/')));
  } catch {
    return null;
  }
}

export const isTokenExpired = (): boolean => {
  const token = getToken();
  if (!token) return true;
  const payload = parseJwtPayload(token);
  if (!payload || typeof payload.exp !== 'number') return true;
  // 30-second buffer
  return payload.exp * 1000 < Date.now() + 30_000;
};

export const getUserInfo = () => {
  const token = getToken();
  if (!token) return null;
  const p = parseJwtPayload(token);
  if (!p) return null;
  return {
    id: p.sub as string | undefined,
    username: p.preferred_username as string | undefined,
    email: p.email as string | undefined,
    firstName: p.given_name as string | undefined,
    lastName: p.family_name as string | undefined,
    roles: (p.realm_access as { roles?: string[] } | undefined)?.roles ?? [],
  };
};

// ---- Auth actions ----

export const login = (redirectPath?: string) => {
  const postLoginPath =
    redirectPath ?? window.location.pathname + window.location.search;
  const isAuthCallback = postLoginPath.includes('/callback');
  if (!isAuthCallback) {
    sessionStorage.setItem('loginRedirect', postLoginPath);
  }

  const state = crypto.randomUUID();
  const redirectUri = encodeURIComponent(`${window.location.origin}/callback`);
  window.location.href = `${IAM_URL}/oauth2/authorize?redirect_uri=${redirectUri}&state=${state}`;
};

export const logout = () => {
  const idToken = sessionStorage.getItem(ID_TOKEN_KEY);
  clearTokens();
  const logoutUrl =
    `${KEYCLOAK_URL}/realms/${REALM}/protocol/openid-connect/logout` +
    `?client_id=${CLIENT_ID}` +
    `&post_logout_redirect_uri=${encodeURIComponent(window.location.origin)}` +
    (idToken ? `&id_token_hint=${encodeURIComponent(idToken)}` : '');
  window.location.href = logoutUrl;
};

export const register = () => {
  const redirectUri = encodeURIComponent(window.location.origin);
  window.location.href =
    `${KEYCLOAK_URL}/realms/${REALM}/protocol/openid-connect/registrations` +
    `?client_id=${CLIENT_ID}&response_type=code&redirect_uri=${redirectUri}`;
};

/**
 * Tries to exchange the stored refresh token for a new access token.
 * Returns true on success. Throws on failure.
 */
export const updateToken = async (
  minValidity: number = 30
): Promise<boolean> => {
  const refreshToken = getRefreshToken();
  if (!refreshToken) {
    throw new Error('No refresh token available');
  }

  // Skip if access token still has enough lifetime
  const token = getToken();
  if (token) {
    const payload = parseJwtPayload(token);
    if (payload && typeof payload.exp === 'number') {
      const remaining = payload.exp * 1000 - Date.now();
      if (remaining > minValidity * 1000) {
        return false; // still valid, no refresh needed
      }
    }
  }

  const tokenUrl = `${KEYCLOAK_URL}/realms/${REALM}/protocol/openid-connect/token`;
  const body = new URLSearchParams({
    grant_type: 'refresh_token',
    client_id: CLIENT_ID,
    refresh_token: refreshToken,
  });

  const res = await fetch(tokenUrl, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: body.toString(),
  });

  if (!res.ok) {
    clearTokens();
    throw new Error('Token refresh failed');
  }

  const data = (await res.json()) as {
    access_token: string;
    refresh_token: string;
    id_token: string;
  };
  storeTokens(
    data.access_token,
    data.refresh_token ?? refreshToken,
    data.id_token ?? ''
  );
  return true;
};

// ---- Role helpers ----

export const hasRole = (role: string): boolean => {
  const info = getUserInfo();
  return info?.roles.includes(role) ?? false;
};

export const hasAnyRole = (roles: string[]): boolean => roles.some(hasRole);
export const hasAllRoles = (roles: string[]): boolean => roles.every(hasRole);
